import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { debounceTime } from 'rxjs/operators'
import { BehaviorSubject } from 'rxjs'

import { setItem } from '../../../global/data/synch-storage.js'
import { ESC_PROFILE_KEY } from '../../../global/authentication/authentication-manager.js'

import {
  ACTIVE,
  INACTIVE,
  SERVICE_LABEL_ADVISOR,
  SERVICE_LABEL_WHOLESALER,
  SERVICE_LABEL_USER,
  SERVICE_LABEL_WHOLESALER_ACTOR,
  SERVICE_LABEL_INVESTOR,
} from './Helper/constants.js'
import {
  findFundById,
  getAllSubscriptionsFromSubscriptionProfile,
  getSubscribedLiteratureForFunds,
  updateSubscriptionData,
  updateFundData,
} from './Helper/helper.js'

import YourSubscriptions from './YourSubscriptions/YourSubscriptions.jsx'
import Insights from './Insights/Insights.jsx'
import PendingVerificationModal from './Modules/PendingVerificationModal.jsx'
import Products from './Products/Products.jsx'
import Profile from './Profile/Profile.jsx'
import Preferences from './Preferences/Preferences.jsx'

import {
  getSubscriptions,
  getSubscriptionFunds,
  updateSubscriptions,
  updateProfile,
  updateProfileAdvisor,
  getStorageProfile,
  setStorageProfile,
  getSubscriptionsForUser,
} from '../Data/SubscriptionCenterDataSource.js'

import './index.css'

const serviceDataQueue = new Map()
const inputStream = new BehaviorSubject(new Map())
const DELAY_FOR_SERVICE_CALL = 1000

function EmailSubscriptionCenterLanding(props) {
  const [subscriptions, setSubscriptions] = useState([])
  const [funds, setFunds] = useState([])
  const [userSubscriptionProfile, setUserSubscriptionProfile] = useState(
    props.userSubscriptionProfile
  )
  const [showModal, setShowModal] = useState(
    (localStorage.getItem('cti-pending-verification-modal') || '').indexOf(
      'yes'
    ) > -1
  )

  const { productTypes, assetClasses, wholesalerLabels } = props

  const storageProfile = getStorageProfile()

  useEffect(() => {
    initializeData()
  }, [])

  useEffect(() => {
    const sub = inputStream
      .pipe(debounceTime(DELAY_FOR_SERVICE_CALL))
      .subscribe((serviceMap) => {
        if (serviceMap.size > 0) {
          const postData = {
            subscriptions: [...serviceMap.values()],
          }

          serviceDataQueue.clear()

          updateSubscriptions(postData)
        }
      })
    return () => {
      sub.unsubscribe()
    }
  }, [subscriptions])

  const initializeData = async () => {
    let _subscriptions = await getSubscriptions()

    _subscriptions = updateSubscriptionData(
      userSubscriptionProfile,
      _subscriptions
    )

    setSubscriptions(_subscriptions)

    let _funds = await getSubscriptionFunds()

    _funds = updateFundData(_funds.funds, userSubscriptionProfile)

    setFunds(_funds)
  }

  const handleSubscribe = (event) => {
    updateUnsubscribeState()

    const _subscriptions = JSON.parse(JSON.stringify(subscriptions))

    const subscriptionId = event.target.value

    const checked =
      (event.target.checked !== undefined && event.target.checked) ||
      String(event.target.dataset.checked) === 'true'

    const subscription = findSubscriptionById(_subscriptions, subscriptionId)

    subscription.checked = checked

    setSubscriptions(_subscriptions)

    addToServiceQueue([
      {
        id: subscription.id,
        selected: subscription.checked,
      },
    ])
  }

  const handleUnsubscribeSubscriptions = async () => {
    const _subscriptions = JSON.parse(JSON.stringify(subscriptions))
    _subscriptions.forEach((subscription) => {
      if (subscription.checked) {
        subscription.checked = false
      }
    })
    setSubscriptions(_subscriptions)

    const _funds = JSON.parse(JSON.stringify(funds))
    _funds.forEach((fund) => {
      const literatureDocs = getSubscribedLiteratureForFunds(fund)

      if (literatureDocs.length > 0) {
        fund.selected = false
        getFundSubscriptions(fund)
      }
    })
    setFunds(_funds)

    const subscriptionProfile = (await getSubscriptionsForUser()).value

    const allSubs =
      getAllSubscriptionsFromSubscriptionProfile(subscriptionProfile)
    const subscriptionsToUpdate = allSubs
      .filter((sub) => sub.status === ACTIVE)
      .map((sub) => {
        return sub.publication || sub.publication
      })

    subscriptionsToUpdate.forEach((subscription) => {
      addToServiceQueue([
        {
          id: subscription.publicationID,
          selected: false,
        },
      ])
    })
  }

  const handleUnsubscribeAll = async () => {
    handleUnsubscribeSubscriptions()

    const userProfile = {
      contactID: userSubscriptionProfile.contactID,
      firstName: userSubscriptionProfile.firstName,
      lastName: userSubscriptionProfile.lastName,
      emailId: userSubscriptionProfile.emailId,
      role: userSubscriptionProfile.role,
      subscriptionsEnabled: 'N',
      emailOptoutEnabled: 'Y',
    }

    handleUpdateProfile(userProfile, true)
  }

  const getFundSubscriptions = (fund) => {
    const fundSubscriptions = []

    fund.activeLiterature.forEach((doc) => {
      doc.selected = false
      doc.preSelected = false

      fundSubscriptions.push({ id: doc.id, selected: false })
    })

    return fundSubscriptions
  }

  const handleUnsubscribeFund = (event) => {
    const fundId = event.target.dataset.fundId
    const _funds = JSON.parse(JSON.stringify(funds))
    const fund = findFundById(_funds, fundId)

    const fundSubscriptions = []
    const originalFund = findFundById(funds, fundId)
    originalFund.activeLiterature.map((doc) => {
      if (doc.selected) {
        fundSubscriptions.push({ id: doc.id, selected: false })
      }
    })

    fund.selected = false

    getFundSubscriptions(fund)
    addToServiceQueue(fundSubscriptions)

    setFunds(_funds)
  }

  const addToServiceQueue = (subscribedSubscriptions) => {
    subscribedSubscriptions.map((subscription) => {
      const serviceObject = {
        wholeSalerID: storageProfile.wholeSalerID || null,
        contactID: userSubscriptionProfile.contactID,
        publicationID: subscription.id,
        status: subscription.selected ? ACTIVE : INACTIVE,
        actor: storageProfile.wholeSalerID
          ? SERVICE_LABEL_WHOLESALER_ACTOR
          : userSubscriptionProfile.role === SERVICE_LABEL_ADVISOR
            ? SERVICE_LABEL_USER
            : SERVICE_LABEL_INVESTOR,
        updatedTimeStamp: new Date().toISOString(),
        firstName: storageProfile.firstName,
        lastName: storageProfile.lastName,
        userId: storageProfile.emailId,
      }

      serviceDataQueue.set(subscription.id, serviceObject)
    })

    inputStream.next(serviceDataQueue)
  }

  const handleManageFundClick = (event) => {
    let fundId = 'fundId' + event.target.dataset.fundId
    let mobileView = false

    // mobile view has different jump calculations
    if (
      event.target.className &&
      event.target.className.indexOf('u-lg-hidden') >= 0
    ) {
      mobileView = true
      fundId = 'mobileFundId' + event.target.dataset.fundId
    }

    const HARD_CODED_MARGIN = -150
    const element = document.getElementById(fundId)
    if (element) {
      let scrollPosition = 0
      if (mobileView) {
        scrollPosition = element.offsetTop + HARD_CODED_MARGIN
      } else {
        const index = element.dataset.index
        const parentTop = element.offsetParent.offsetTop
        const height = element.offsetHeight
        const top = parentTop + height * index
        scrollPosition = top + HARD_CODED_MARGIN
      }
      window.scrollTo(0, scrollPosition)
    }
  }

  const updateFunds = (_funds) => {
    if (
      _funds.find(
        (fund) =>
          fund.activeLiterature &&
          fund.activeLiterature.find((lit) => lit && lit.selected)
      )
    ) {
      updateUnsubscribeState()
    }
    setFunds(_funds)
  }

  const handleUpdateProfile = (userProfile, secure) => {
    // Updating `subscriptionsEnabled` from Preferences
    if (secure) {
      const _userSubscriptionProfile = { ...userSubscriptionProfile }

      _userSubscriptionProfile.subscriptionsEnabled =
        userProfile.subscriptionsEnabled

      if (userProfile.emailOptoutEnabled === null) {
        userProfile.emailOptoutEnabled = 'N'
      }
      _userSubscriptionProfile.emailOptoutEnabled =
        userProfile.emailOptoutEnabled

      setUserSubscriptionProfile(_userSubscriptionProfile)

      updateProfileAdvisor(userProfile)
      // Updating `firstName`, `lastName`, `emailId` from Profile
      // ...which is also stored in ESC_PROFILE and userSubscriptionProfile
    } else {
      // Synchronize with ESC_PROFILE

      if (storageProfile) {
        storageProfile.firstName = userProfile.firstName
        storageProfile.lastName = userProfile.lastName
        storageProfile.emailId = userProfile.emailId

        setStorageProfile(JSON.stringify(storageProfile))
        setItem(ESC_PROFILE_KEY, JSON.stringify(storageProfile))
      }

      // Synchronize with userSubscriptionProfile
      const _userSubscriptionProfile = { ...userSubscriptionProfile }

      _userSubscriptionProfile.firstName = userProfile.firstName
      _userSubscriptionProfile.lastName = userProfile.lastName
      _userSubscriptionProfile.emailId = userProfile.emailId

      setUserSubscriptionProfile(_userSubscriptionProfile)

      // Update using service
      updateProfile(userProfile)
    }
  }

  const handleVerificationModalDismiss = () => {
    localStorage.removeItem('cti-pending-verification-modal')

    setShowModal(false)
  }

  const updateUnsubscribeState = () => {
    // the service will reset this back to 'N', so we can update our state to do so, too
    if (userSubscriptionProfile.emailOptoutEnabled === 'Y') {
      const _userSubscriptionProfile = { ...userSubscriptionProfile }
      _userSubscriptionProfile.emailOptoutEnabled = 'N'
      setUserSubscriptionProfile(_userSubscriptionProfile)
    }
  }

  const removeThermostatFund = (payload) => {
    return payload.map((i) =>
      i.name === 'Columbia Thermostat Fund'
        ? {
            ...i,
            activeLiterature: i?.activeLiterature.filter(
              (f) => f.name !== 'Thermostat Fund Asset Allocation Update'
            ),
          }
        : i
    )
  }

  return (
    <div className="esc-scope">
      <div className="Grid u-sizeConstrained u-paddingVert8gu">
        <div className="u-lg-size3of12 u-paddingRight10gu u-sm-sizeFull u-sm-paddingHoriz0">
          <Profile
            userSubscriptionProfile={userSubscriptionProfile}
            storageProfile={storageProfile}
            handleUpdateProfile={handleUpdateProfile}
          />
        </div>
        <div className="u-lg-size9of12 u-sm-sizeFull">
          <YourSubscriptions
            subscriptions={subscriptions}
            productTypes={productTypes}
            wholesalerLabels={wholesalerLabels}
            funds={removeThermostatFund(funds)}
            userSubscriptionProfile={userSubscriptionProfile}
            handleSubscribe={handleSubscribe}
            handleUnsubscribeSubscriptions={handleUnsubscribeSubscriptions}
            handleUnsubscribeFund={handleUnsubscribeFund}
            handleManageFundClick={handleManageFundClick}
          />
          <Insights
            subscriptions={subscriptions}
            wholesalerLabels={wholesalerLabels}
            userSubscriptionProfile={userSubscriptionProfile}
            handleSubscribe={handleSubscribe}
          />
          <Products
            subscriptions={subscriptions}
            funds={removeThermostatFund(funds)}
            productTypes={productTypes}
            assetClasses={assetClasses}
            wholesalerLabels={wholesalerLabels}
            userSubscriptionProfile={userSubscriptionProfile}
            updateFunds={updateFunds}
            addToServiceQueue={addToServiceQueue}
            handleUnsubscribeFund={handleUnsubscribeFund}
            handleSubscribe={handleSubscribe}
          />
          {(userSubscriptionProfile.role === SERVICE_LABEL_ADVISOR ||
            userSubscriptionProfile.role === SERVICE_LABEL_WHOLESALER) && (
            <Preferences
              userSubscriptionProfile={userSubscriptionProfile}
              storageProfile={storageProfile}
              handleUpdateProfile={handleUpdateProfile}
              handleUnsubscribeAll={handleUnsubscribeAll}
            />
          )}
        </div>
      </div>
      {userSubscriptionProfile.role === SERVICE_LABEL_ADVISOR &&
        userSubscriptionProfile.contactStatusCd === 'Pending' &&
        showModal && (
          <PendingVerificationModal
            storageProfile={storageProfile}
            handleDismiss={handleVerificationModalDismiss}
          />
        )}
    </div>
  )
}

const findSubscriptionById = (subscriptions, id) => {
  return subscriptions.find((subscription) => subscription.id === id)
}

EmailSubscriptionCenterLanding.propTypes = {
  userSubscriptionProfile: PropTypes.object.isRequired,
  productTypes: PropTypes.array.isRequired,
  assetClasses: PropTypes.array.isRequired,
  wholesalerLabels: PropTypes.array.isRequired,
}

export default EmailSubscriptionCenterLanding
